import React, { useEffect } from "react";
import { FiAlertTriangle, FiUsers, FiShield, FiCheckCircle } from "react-icons/fi";
import { Button } from "@/components/ui/button";

export const RidePreferenceWarningModal = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
}) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !loading && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <div
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-zinc-200 overflow-hidden transform transition-all animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="warning-modal-title"
      >
        {/* Header decoration bar */}
        <div className="h-2 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600" />

        <div className="p-6 sm:p-7">
          {/* Icon & Title */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 border border-amber-200 text-amber-600 flex items-center justify-center shrink-0 shadow-inner">
              <FiAlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full mb-1">
                Preference Update Warning
              </span>
              <h2
                id="warning-modal-title"
                className="text-xl sm:text-2xl font-bold text-zinc-900 leading-snug"
              >
                Open Ride to Any Gender?
              </h2>
            </div>
          </div>

          {/* Explanation Box */}
          <div className="mt-5 p-4 rounded-2xl bg-amber-50/80 border border-amber-200/90 text-zinc-800 space-y-2">
            <p className="text-sm font-semibold text-amber-950">
              You are about to make this ride <span className="underline decoration-amber-500 decoration-2">unbiased and open to all passengers</span>.
            </p>
            <p className="text-xs sm:text-sm text-zinc-700 leading-relaxed">
              If you haven't found female co-passengers yet, opening your ride allows passengers of any gender to view and request to join.
            </p>
          </div>

          {/* Key Changes Checklist */}
          <div className="mt-4 space-y-2.5 text-xs sm:text-sm text-zinc-700">
            <div className="flex items-start gap-2.5">
              <FiUsers className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span>
                <strong>Any Gender Can Apply:</strong> Male, female, and other students/faculty will be eligible to send join requests.
              </span>
            </div>
            <div className="flex items-start gap-2.5">
              <FiCheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span>
                <strong>Host Retains Approval:</strong> You still have full authority to accept or decline any join request.
              </span>
            </div>
            <div className="flex items-start gap-2.5">
              <FiShield className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />
              <span>
                <strong>Female-Only Badge Removed:</strong> The ride will no longer be marked exclusively for female passengers.
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-7 flex flex-col-reverse sm:flex-row items-center justify-end gap-3 pt-3 border-t border-zinc-100">
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              onClick={onClose}
              className="w-full sm:w-auto rounded-xl px-5 py-2.5 border-zinc-300 text-zinc-700 hover:bg-zinc-100 font-medium text-sm transition-colors"
            >
              Keep Female Only
            </Button>
            <Button
              type="button"
              disabled={loading}
              onClick={onConfirm}
              className="w-full sm:w-auto rounded-xl px-5 py-2.5 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-semibold text-sm shadow-md transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Updating...</span>
                </>
              ) : (
                <span>Confirm & Open to Anyone</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RidePreferenceWarningModal;
